/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router-dom components

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/system";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";

// sweetalert2 components
import Swal from "sweetalert2";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import LanguagesModal from "Modals/Languages";
import EditTranslations from "Modals/EditTranslations";
import CheckObjectDeletion from "Modals/CheckObjectDeletion";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

import { getData, putData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";

import { useSoftUIController } from "context";

import Cookies from "js-cookie";

// Badges
const disabledTemplate = (
  <SoftBadge variant="contained" color="error" size="xs" badgeContent="Inactive" container />
);
const enabledTemplate = (
  <SoftBadge variant="contained" color="success" size="xs" badgeContent="Active" container />
);

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

let lastSavedData = {};
let lastSportSelected = null;

/**
 *
 * @returns
 */
function MarketsTranslationsList() {
  const abortController = new AbortController();

  const [controller, dispatch] = useSoftUIController();
  const { platform } = controller;

  const [showEditModal, setCreateModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [showLanguagesModal, setLanguagesModal] = useState(false);
  const [updateRecords, setUpdateRecords] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(0);
  const [templateCreatedId, setTemplateCreatedId] = useState(0);
  const [hiddenColumnsArray, setHiddenColumns] = useState(null);
  const [dataTableData, setDataTableData] = useState(getInitData);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [rowDataSelected, setRowDataSelected] = useState(null);
  const [sportSelected, setSportSelected] = useState(null);
  const [sports, setSports] = useState([]);
  const [updating, setUpdating] = useState(false);

  const userData = getUserData();
  let userId = userData.id;

  function createColumn(label) {
    return { Header: label, accessor: label.toLowerCase(), width: "350px", Cell: editableCell };
  }

  function getInitData(status) {
    //console.log("getInitData...");
    //if (status) console.log("from initData");
    return {
      columns: [
        {
          Header: "Sport",
          accessor: "sport",
          width: "50px",
        },
        {
          Header: "Type",
          accessor: "type",
          width: "50px",
        },
        {
          Header: "Original Name",
          accessor: "original",
          width: "150px",
          Cell: linkCell,
          editable: false,
        },
        { Header: "English", accessor: "english", width: "350px", Cell: editableCell },
      ],
      rows: [],
    };
  }

  // Create an editable cell renderer
  function editableCell({ value, ...rest }) {
    return (
      <>
        <SoftBox
          className="translations-cell"
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          p={0}
        >
          {/* <SoftTypography>{value ? value : ""}</SoftTypography> */}
          <SoftInput
            key={
              "market-" +
              rest.row.cells[2].value.toLowerCase().replace(" ", "_") +
              "-editable-lang-" +
              rest.column.Header.toLowerCase() +
              "-" +
              value
            }
            id={
              "market-" +
              rest.row.cells[2].value.toLowerCase().replace(" ", "_") +
              "-editable-lang-" +
              rest.column.Header.toLowerCase()
            }
            defaultValue={value}
            // onChange={({ currentTarget }) => {
            //   //console.log(value, currentTarget.value);
            //   value = currentTarget.value;
            // }}
            onBlur={(e) => updateData(e, rest)}
            onKeyUp={(e) => updateData(e, rest)}
            size="small"
            style={{ width: "50px" }}
            className="editable-cell to-edit"
          />

          <HtmlTooltip placement="right" title={"Reset to Original Translation"}>
            <Button onClick={(e) => updateResetData(e, rest)} className="hidden">
              <Icon>rotate_left_outlined_icon</Icon>
            </Button>
          </HtmlTooltip>
        </SoftBox>
      </>
    );
  }

  // Create an editable cell renderer
  function linkCell({ value, ...rest }) {
    return (
      <>
        <HtmlTooltip placement="right" title={"Update all Translations"}>
          <Button onClick={(e) => handleEditOpenModal(e, rest)} className="to-edit">
            <SoftTypography
              component="span"
              variant="button"
              fontWeight="regular"
              color={"info"}
              opacity={0.8}
              sx={{ lineHeight: 0 }}
            >
              {value}
            </SoftTypography>
          </Button>
        </HtmlTooltip>
        <Icon className="hidden">edit_outlined_icon</Icon>
      </>
    );
  }

  async function updateResetData(e, rowData) {
    e.currentTarget.value = "reset";
    updateData(e, rowData);
  }

  async function updateData(e, rowData) {
    e.preventDefault();
    e.stopPropagation();
    setUpdating(true);
    if (updating) return;

    if (e._reactName == "onKeyUp" && e.code != "Enter") return;

    let tmpTableData = JSON.parse(JSON.stringify(lastSavedData));

    let row = lastSavedData.rows.find((item) => item.original == rowData.row.cells[2].value);

    if (row[rowData.column.Header.toLowerCase()] == e.currentTarget.value) return;

    let updateCompetitionsTranslationsEndpoint = `/api/v1/translations/clientmarkets`;
    let data = {
      lang: rowData.column.Header,
      translation: e.currentTarget.value,
      original: rowData.row.cells[2].value,
      platform_id: platform.value,
      uni_id: rowData.row.original[1].uni_id,
    };

    let response = null;
    if (data.lang && data.translation && data.original && data.platform_id) {
      setUpdating(true);
      response = await putData(updateCompetitionsTranslationsEndpoint, data, {
        Authorization: getAuthorizationHeader(),
      });
    }

    if (response) {
      setUpdating(false);
      initData(false, false);
    }
  }

  /**
   *
   */
  const handleLanguages = (languages) => {
    setHiddenColumns(languages);
    if (showLanguagesModal) setLanguagesModal(false);
    if (!showLanguagesModal) setLanguagesModal(true);

    Cookies.set("settings-translations-markets-" + userId, languages, {
      expires: 7,
      path: "/",
      sameSite: "Lax",
    });
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleCloseLanguagesModal = async (update, name) => {
    if (showLanguagesModal) setLanguagesModal(false);
    if (!showLanguagesModal) setLanguagesModal(true);
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleEditCloseModal = async (update, name) => {
    if (update) {
      setRowDataSelected(null);
      //console.log("Creation completed, please Update!");
      initData(false, true);
    } else {
      setRowDataSelected(null);
      //console.log("Creation canceled, do nothing!");
    }
    setCreateModal(false);
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleEditOpenModal = (e, rowData) => {
    e.preventDefault();
    setCreateModal(false);
    setRowDataSelected(null);
    let selectedItem = rowData.row.original; //rowData.data.find((item) => item.original == e.currentTarget.textContent);

    setRowDataSelected(selectedItem);
    setLanguagesModal(false);
    setCreateModal(true);
  };

  /**
   *
   * @param {*} update
   * @param {*} id
   */
  const handleDeleteCloseModal = async (update, id) => {
    if (update) {
      //console.log("Delete confirmed, please Update!");
      // let setTemplatesEndpoint = `/api/v1/users/${id}`;
      // let response = await delData(setTemplatesEndpoint, {
      //   Authorization: getAuthorizationHeader(),
      // });
      // if (response) {
      //   deleteRecord(id);
      //   initData();
      // }
    } else {
      //console.log("Delete canceled, do nothing!");
    }
    setDeleteModal(false);
  };

  /**
   *
   */
  async function initData(reset, loader) {
    if (!platform || platform == "") return;

    if (loader) {
      Swal.fire({
        title: "We proccess your request! ",
        html: "Wait for few seconds...",
        timerProgressBar: true,
        didOpen: async () => {
          Swal.showLoading();
          const userData = getUserData();
          let userId = userData.id;
          let data = getInitData(true);
          if (reset) {
            setDataTableData(data);
            setHiddenColumns(null);
          }

          let selectedPlatformId = platform.value;
          let getCompetitionTranslationsEndpoint = `/api/v1/translations/clientmarkets/${selectedPlatformId}`;
          let response = await getData(
            getCompetitionTranslationsEndpoint,
            {
              Authorization: getAuthorizationHeader(),
            },
            null,
            abortController ? { signal: abortController.signal } : null
          );
          if (!response) {
            Swal.clickDeny();
          } else {
            if (response.status === 200) {
              let entries = [];
              let languages = [];
              let hiddenlanguages = [];
              let dynamicColumns = [];
              let availableSports = [{ label: "All", value: "All" }];
              if (response != null) {
                entries = Object.entries(response.data);
                entries.map((el) => {
                  el.original = el[1].original;
                  el.sport = el[1].sport;
                  el.type = el[1].type;
                  el.uni_id = el[1].uni_id;
                  for (const lang in el[1]) {
                    if (lang == "type" || lang == "original" || lang == "uni_id") continue;
                    if (lang == "sport") {
                      let sportFound = availableSports.find((item) => item.label == el.sport);
                      if (!sportFound) availableSports.push({ label: el.sport, value: el.sport });
                      continue;
                    }
                    if (Object.hasOwnProperty.call(el[1], lang)) {
                      if (languages.indexOf(lang) < 0) languages.push(lang);

                      const translation = el[1][lang];
                      el[lang.toLowerCase()] = translation;
                      if (lang != "English" && hiddenlanguages.indexOf(lang.toLowerCase()) < 0) {
                        hiddenlanguages.push(lang.toLowerCase());
                        dynamicColumns.push(createColumn(lang));
                      }
                    }
                  }
                });
              } else {
              }

              availableSports.sort((a, b) => (a.label > b.label ? 1 : -1));

              setSports(availableSports);
              languages.sort();

              let switchLanguages = [];
              let cookiesHiddenColumns = Cookies.get("settings-translations-markets-" + userId);

              languages.forEach((lang) => {
                let checked = false;

                if (reset && lang == "English") checked = true;

                if (cookiesHiddenColumns) {
                  if (cookiesHiddenColumns.indexOf(lang.toLowerCase()) > -1) {
                    checked = false;
                  } else {
                    checked = true;
                  }
                }

                switchLanguages.push({ name: lang, checked: checked });
              });

              if (reset) setAvailableLanguages(switchLanguages);
              if (reset) {
                if (cookiesHiddenColumns) {
                  setHiddenColumns(cookiesHiddenColumns.split(","));
                } else {
                  setHiddenColumns(hiddenlanguages);
                }
              }

              data.columns = data.columns.concat(dynamicColumns);
              data.rows = response && response.data ? entries : data.rows;

              //console.log("Init Data");

              setDataTableData(data);
              lastSavedData = data;

              if (!lastSportSelected) {
                setSportSelected({ label: "All", value: "All" });
              } else {
                handleSelectionValueChange(lastSportSelected);
              }
              Swal.clickConfirm();
            }
          }
        },
        willClose: () => {},
        allowOutsideClick: false,
      });
    } else {
      //console.log("initData..");
      const userData = getUserData();
      let userId = userData.id;
      let data = getInitData(true);
      if (reset) {
        setDataTableData(data);
        setHiddenColumns(null);
      }

      let selectedPlatformId = platform.value;
      let getCompetitionTranslationsEndpoint = `/api/v1/translations/clientmarkets/${selectedPlatformId}`;
      let response = await getData(
        getCompetitionTranslationsEndpoint,
        {
          Authorization: getAuthorizationHeader(),
        },
        null,
        abortController ? { signal: abortController.signal } : null
      );
      let entries = [];
      let languages = [];
      let hiddenlanguages = [];
      let dynamicColumns = [];
      let availableSports = [{ label: "All", value: "All" }];
      if (response != null) {
        entries = Object.entries(response.data);
        entries.map((el) => {
          el.original = el[1].original;
          el.sport = el[1].sport;
          el.type = el[1].type;
          el.uni_id = el[1].uni_id;
          for (const lang in el[1]) {
            if (lang == "type" || lang == "original" || lang == "uni_id") continue;
            if (lang == "sport") {
              let sportFound = availableSports.find((item) => item.label == el.sport);
              if (!sportFound) availableSports.push({ label: el.sport, value: el.sport });
              continue;
            }
            if (Object.hasOwnProperty.call(el[1], lang)) {
              if (languages.indexOf(lang) < 0) languages.push(lang);

              const translation = el[1][lang];
              el[lang.toLowerCase()] = translation;
              if (lang != "English" && hiddenlanguages.indexOf(lang.toLowerCase()) < 0) {
                hiddenlanguages.push(lang.toLowerCase());
                dynamicColumns.push(createColumn(lang));
              }
            }
          }
        });
      } else {
      }

      availableSports.sort((a, b) => (a.label > b.label ? 1 : -1));

      setSports(availableSports);
      languages.sort();

      let switchLanguages = [];
      let cookiesHiddenColumns = Cookies.get("settings-translations-markets-" + userId);

      languages.forEach((lang) => {
        let checked = false;

        if (reset && lang == "English") checked = true;

        if (cookiesHiddenColumns) {
          if (cookiesHiddenColumns.indexOf(lang.toLowerCase()) > -1) {
            checked = false;
          } else {
            checked = true;
          }
        }

        switchLanguages.push({ name: lang, checked: checked });
      });

      if (reset) setAvailableLanguages(switchLanguages);
      if (reset) {
        if (cookiesHiddenColumns) {
          setHiddenColumns(cookiesHiddenColumns.split(","));
        } else {
          setHiddenColumns(hiddenlanguages);
        }
      }

      data.columns = data.columns.concat(dynamicColumns);
      data.rows = response && response.data ? entries : data.rows;

      //console.log("Init Data");
      setDataTableData(data);
      lastSavedData = data;
      if (!lastSportSelected) {
        setSportSelected({ label: "All", value: "All" });
      } else {
        handleSelectionValueChange(lastSportSelected);
      }
    }
  }

  /**
   *
   * @param {*} id
   */
  function deleteRecord(id) {
    //console.log("deleteRecord:", id);
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];

    let indexToRemove = initDataRows.findIndex((template) => template.id === id);
    initDataRows.splice(indexToRemove, 1);

    initData.rows = initDataRows;
    setDataTableData(initData);
  }

  const handleSelectionValueChange = (item) => {
    setSportSelected(item);
    lastSportSelected = item;

    let columns = [...lastSavedData.columns];
    let rows = [...lastSavedData.rows];

    if (item.label != "All") rows = rows.filter((row) => row.sport === item.label);

    let data = { columns: columns, rows: rows };

    setDataTableData(data);
  };

  const handleTableDataChanges = async ({ key, value, extra }) => {
    //console.log(key, ":", value, extra);
    if (key == "refresh") {
      initData(true, true);
    }
  };

  useEffect(() => {
    initData(true, true);
  }, [platform.label]);

  useEffect(() => {
    lastSportSelected = null;
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LanguagesModal
        languages={availableLanguages}
        showModal={showLanguagesModal}
        signalCloseModal={handleCloseLanguagesModal}
        setAvailableLanguages={handleLanguages}
      ></LanguagesModal>
      <EditTranslations
        showModal={showEditModal}
        signalCloseModal={handleEditCloseModal}
        data={rowDataSelected}
        entity="market"
      ></EditTranslations>
      <CheckObjectDeletion
        referenceId={recordToDelete}
        reference={"users"}
        showModal={showDeleteModal}
        signalCloseModal={handleDeleteCloseModal}
      ></CheckObjectDeletion>
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Markets Translations
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can Manage all the available translations for Markets
              </SoftTypography>
            </SoftBox>
            <Stack spacing={1} direction="row">
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handleCloseLanguagesModal}
              >
                Language Columns
              </SoftButton>
            </Stack>
          </SoftBox>
          <SoftBox p={3} sx={{ paddingTop: "0px !important" }}>
            <Grid container spacing={3}>
              <Grid item xs={3} md={3}>
                <SoftSelect
                  value={sportSelected}
                  id="trans-sport-filter"
                  size="small"
                  options={sports}
                  onChange={handleSelectionValueChange}
                />
              </Grid>
            </Grid>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            hiddenColumns={hiddenColumnsArray}
            canSearch
            refreshEnabled={true}
            informParent={handleTableDataChanges}
            bodyClassName="market-editable"
            headerClassName="market-editable"
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default MarketsTranslationsList;
